import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ValidationErrorInterface } from '@zaoblako/shared/common/interfaces/validation-error.interface';
import { DealOrderFindInterface } from '@zaoblako/shared/modules/business/dto/deal-order/deal-order-find.dto';
import { DealOrderFormInterface } from '@zaoblako/shared/modules/business/dto/deal-order/deal-order-form.dto';
import type { NomenclatureItemFindInterface } from '@zaoblako/shared/modules/catalog/dtos/nomenclature/nomenclature-item-find.dto';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { NomenclatureItemAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/nomenclature-item-auto-complete/nomenclature-item-auto-complete.component';
import { TableComponent } from '@zaoblako/web/shared/dumbs/table/table.component';
import { JoinWithChildPipe } from '@zaoblako/web/shared/pipes/join-with-child.pipe';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { SumPipe } from '@zaoblako/web/shared/pipes/sum.pipe';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputNumberModule } from 'primeng/inputnumber';

@Component({
  standalone: true,
  selector: 'crm-deal-order-form',
  templateUrl: './deal-order-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    TableComponent,
    InputNumberModule,
    FormsModule,
    ButtonModule,
    JoinPipe,
    JoinWithChildPipe,
    SumPipe,
    NomenclatureItemAutoCompleteComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DealOrderFormComponent {
  @Input() public orders: DealOrderFormInterface[] = [];
  @Input() public errors: Array<ValidationErrorInterface<DealOrderFormInterface>>;

  @Output() public ordersChange = new EventEmitter<DealOrderFormInterface[]>();

  public columns: CrmTableColumn[] = [
    { label: 'Номенклатура' },
    { label: 'Количество' },
    { label: 'Цена' },
    { label: 'Сумма' },
    { class: 'w-2rem' }
  ];

  public isShowNomenclatures = false;
  public selectedNomenclature: NomenclatureItemFindInterface = null;
  public excludeIds: number[] = [];

  public constructor(private readonly cdRef: ChangeDetectorRef) {}

  public toOrder(order: any): DealOrderFormInterface {
    return order as DealOrderFormInterface;
  }

  public addOrders() {
    if (!this.orders) {
      this.orders = [];
    }
    if (this.selectedNomenclature) {
      this.orders.push({
        nomenclatureItem: this.selectedNomenclature,
        price: this.selectedNomenclature.price,
        amount: this.selectedNomenclature.price,
        count: 1
      } as DealOrderFindInterface);
      this.orders = [...this.orders];
      this.selectedNomenclature = null;
      this.isShowNomenclatures = false;
      this.cdRef.markForCheck();
    }
    this.ordersChange.emit(this.orders);
  }

  public deleteOrder(order: DealOrderFormInterface) {
    this.orders = this.orders.filter((_order) => order.nomenclatureItem?.id !== _order.nomenclatureItem?.id);
    this.isShowNomenclatures = false;
    this.ordersChange.emit(this.orders);
  }

  public changePrice(order: DealOrderFormInterface, price: number) {
    order.price = isNaN(price) || !price ? 0 : price;
    order.amount = order.count * order.price;
    this.orders = [...this.orders];
    this.ordersChange.emit(this.orders);
  }

  public changeCount(order: DealOrderFormInterface, count: number) {
    order.count = isNaN(count) || !count ? 0 : count;
    order.amount = order.count * order.price;
    this.orders = [...this.orders];
    this.ordersChange.emit(this.orders);
  }

  public toggleAddNomenclature() {
    this.excludeIds = this.orders
      ?.filter((order: DealOrderFindInterface) => order?.nomenclatureItem)
      ?.map((order: DealOrderFindInterface) => order.nomenclatureItem?.id);
    this.isShowNomenclatures = true;
    this.cdRef.markForCheck();
  }
}
